import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, ChevronDown, ChevronLeft, ChevronRight,
  ChevronUp, Download, Globe, Mail, Phone as PhoneIcon, Smartphone,
} from 'lucide-react';
import API from '../../lib/api';
import AdminFilterBar from './AdminFilterBar';

const STATUS_COLORS = {
  success: 'bg-green-100 text-green-700',
  failure: 'bg-red-100 text-red-600',
};

function formatRelative(dateStr) {
  if (!dateStr) return '–';
  const d = new Date(dateStr);
  const diffMs = Date.now() - d.getTime();
  if (diffMs < 0) return d.toLocaleString('en-IN');
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return d.toLocaleDateString('en-IN');
}

function shortUA(ua) {
  if (!ua) return '–';
  // Pick the most useful fragment for at-a-glance browsing.
  const m = ua.match(/(Chrome|Firefox|Safari|Edge|Opera|MSIE|Trident)[\/ ]?[\d.]*/);
  const os = ua.match(/(Windows NT [\d.]+|Mac OS X [\d_.]+|Android [\d.]+|iPhone OS [\d_]+|Linux)/);
  return [m?.[0], os?.[0]].filter(Boolean).join(' · ') || ua.slice(0, 60);
}

function LogRow({ log }) {
  const [open, setOpen] = useState(false);
  const hasDetail = log.old_value || log.new_value || log.user_agent;
  return (
    <>
      <tr
        className="hover:bg-[#F5F4FB] transition-colors cursor-pointer"
        onClick={() => hasDetail && setOpen((o) => !o)}
      >
        <td className="px-4 py-3 text-xs text-[#9CA3AF] whitespace-nowrap" title={new Date(log.created_at).toLocaleString('en-IN')}>
          {formatRelative(log.created_at)}
        </td>
        <td className="px-4 py-3">
          <span className="inline-block text-[10px] bg-[#6C63FF]/10 text-[#6C63FF] px-2 py-0.5 rounded font-mono">
            {log.action}
          </span>
        </td>
        <td className="px-4 py-3 text-[#1A1A2E]">{log.description || '–'}</td>
        <td className="px-4 py-3 text-xs text-[#6B7280]">
          {log.entity_type ? (
            <span>
              <span className="text-[#9CA3AF]">{log.entity_type}</span>
              {log.entity_id && <span className="font-mono"> #{log.entity_id}</span>}
            </span>
          ) : '–'}
        </td>
        <td className="px-4 py-3 text-xs text-[#6B7280] font-mono whitespace-nowrap">{log.ip_address || '–'}</td>
        <td className="px-4 py-3">
          <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[log.status] || 'bg-gray-100 text-gray-600'}`}>
            {log.status}
          </span>
        </td>
        <td className="px-4 py-3 text-[#9CA3AF]">
          {hasDetail ? (open ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />) : null}
        </td>
      </tr>
      {open && (
        <tr className="bg-[#FAFAFD]">
          <td colSpan={7} className="px-4 py-3">
            <div className="grid md:grid-cols-2 gap-3">
              {log.user_agent && (
                <div>
                  <p className="text-[10px] font-heading font-semibold text-[#9CA3AF] uppercase tracking-wider mb-1">User Agent</p>
                  <p className="text-xs text-[#1A1A2E] font-mono break-all">{log.user_agent}</p>
                </div>
              )}
              {log.old_value && (
                <div>
                  <p className="text-[10px] font-heading font-semibold text-[#9CA3AF] uppercase tracking-wider mb-1">Before</p>
                  <pre className="text-[11px] text-[#1A1A2E] bg-white border border-[#E8E6F8] rounded p-2 overflow-x-auto">
                    {typeof log.old_value === 'string' ? log.old_value : JSON.stringify(log.old_value, null, 2)}
                  </pre>
                </div>
              )}
              {log.new_value && (
                <div>
                  <p className="text-[10px] font-heading font-semibold text-[#9CA3AF] uppercase tracking-wider mb-1">After</p>
                  <pre className="text-[11px] text-[#1A1A2E] bg-white border border-[#E8E6F8] rounded p-2 overflow-x-auto">
                    {typeof log.new_value === 'string' ? log.new_value : JSON.stringify(log.new_value, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

export default function CustomerLogDetail() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [filters, setFilters] = useState({ search: '', action: '', status: '', from: '', to: '' });
  const [page, setPage]       = useState(1);
  const [data, setData]       = useState({
    user: null, summary: null, logs: [], actions: [], total: 0, pages: 1,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = { page, limit: 50 };
      if (filters.action) params.action      = filters.action;
      if (filters.status) params.status      = filters.status;
      if (filters.from)   params.from        = filters.from;
      if (filters.to)     params.to          = filters.to;
      const { data: res } = await API.get(`/admin/customer-logs/${userId}`, { params });
      setData({
        user:    res.user || null,
        summary: res.summary || null,
        logs:    res.logs || [],
        actions: res.actions || [],
        total:   res.total || 0,
        pages:   res.pages || 1,
      });
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load customer logs.');
    }
    setLoading(false);
  }, [userId, page, filters.action, filters.status, filters.from, filters.to]);

  useEffect(() => { load(); }, [load]);

  const handleFilter = (key, val) => {
    setFilters((f) => ({ ...f, [key]: val }));
    setPage(1);
  };
  const handleReset = () => { setFilters({ search: '', action: '', status: '', from: '', to: '' }); setPage(1); };

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = {};
      if (filters.action) params.action = filters.action;
      if (filters.status) params.status = filters.status;
      if (filters.from)   params.from   = filters.from;
      if (filters.to)     params.to     = filters.to;
      const res = await API.get(`/admin/customer-logs/${userId}/export`, {
        params,
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8' });
      const url  = window.URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = `customer-${userId}-logs.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert(err.response?.data?.detail || 'Export failed.');
    }
    setExporting(false);
  };

  const u = data.user;
  const s = data.summary || {};

  return (
    <div className="space-y-5">
      <button
        onClick={() => navigate('/admin/customer-logs')}
        className="flex items-center gap-1.5 text-sm text-[#6C63FF] hover:underline font-body"
      >
        <ArrowLeft className="h-4 w-4" /> Back to Customer Logs
      </button>

      {/* Customer header */}
      <div className="bg-white rounded-2xl border border-[#E8E6F8] shadow-sm p-5">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-[#6C63FF] flex items-center justify-center text-white text-lg font-bold font-heading">
              {(u?.name || u?.email || '?')[0]?.toUpperCase()}
            </div>
            <div>
              <p className="text-lg font-heading font-bold text-[#1A1A2E]">
                {u?.name || `Customer #${userId}`}
              </p>
              <div className="flex items-center gap-3 text-xs text-[#6B7280] font-body mt-1 flex-wrap">
                {u?.email && <span className="flex items-center gap-1"><Mail className="h-3 w-3" />{u.email}</span>}
                {u?.phone && <span className="flex items-center gap-1"><PhoneIcon className="h-3 w-3" />{u.phone}</span>}
                {u?.created_at && <span>Joined {new Date(u.created_at).toLocaleDateString('en-IN')}</span>}
              </div>
            </div>
          </div>
          <button
            onClick={handleExport}
            disabled={exporting || data.logs.length === 0}
            className="flex items-center gap-1.5 text-xs font-body font-medium px-3 py-2 rounded-lg bg-[#6C63FF]/10 text-[#6C63FF] hover:bg-[#6C63FF]/20 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Download className="h-3.5 w-3.5" />
            {exporting ? 'Exporting…' : 'Export CSV'}
          </button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-5">
          <div className="bg-[#F5F4FB] border border-[#E8E6F8] rounded-xl px-4 py-3">
            <p className="text-[10px] text-[#9CA3AF] font-body uppercase tracking-wider">Total Events</p>
            <p className="text-xl font-heading font-bold text-[#1A1A2E]">{s.total_events ?? 0}</p>
          </div>
          <div className="bg-[#F5F4FB] border border-[#E8E6F8] rounded-xl px-4 py-3">
            <p className="text-[10px] text-[#9CA3AF] font-body uppercase tracking-wider flex items-center gap-1"><Globe className="h-3 w-3" /> Unique IPs</p>
            <p className="text-xl font-heading font-bold text-[#1A1A2E]">{s.unique_ip_count ?? 0}</p>
          </div>
          <div className="bg-[#F5F4FB] border border-[#E8E6F8] rounded-xl px-4 py-3">
            <p className="text-[10px] text-[#9CA3AF] font-body uppercase tracking-wider flex items-center gap-1"><Smartphone className="h-3 w-3" /> Active Days</p>
            <p className="text-xl font-heading font-bold text-[#1A1A2E]">{s.active_days ?? 0}</p>
          </div>
          <div className="bg-[#F5F4FB] border border-[#E8E6F8] rounded-xl px-4 py-3">
            <p className="text-[10px] text-[#9CA3AF] font-body uppercase tracking-wider">Last Seen</p>
            <p className="text-sm font-heading font-bold text-[#1A1A2E]">{formatRelative(s.last_seen_at)}</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <AdminFilterBar filters={filters} onFilterChange={handleFilter} onReset={handleReset}>
        <select
          value={filters.action}
          onChange={(e) => handleFilter('action', e.target.value)}
          className="text-sm font-body border border-[#E8E6F8] rounded-lg px-3 py-2 bg-[#F5F4FB] text-[#1A1A2E] focus:outline-none focus:ring-1 focus:ring-[#6C63FF]/50 max-w-[220px]"
        >
          <option value="">All actions</option>
          {data.actions.map((a) => (
            <option key={a.action} value={a.action}>{a.action} ({a.count})</option>
          ))}
        </select>
        <select
          value={filters.status}
          onChange={(e) => handleFilter('status', e.target.value)}
          className="text-sm font-body border border-[#E8E6F8] rounded-lg px-3 py-2 bg-[#F5F4FB] text-[#1A1A2E] focus:outline-none focus:ring-1 focus:ring-[#6C63FF]/50"
        >
          <option value="">All statuses</option>
          <option value="success">Success</option>
          <option value="failure">Failure</option>
        </select>
        <input
          type="date"
          value={filters.from}
          onChange={(e) => handleFilter('from', e.target.value)}
          className="text-sm font-body border border-[#E8E6F8] rounded-lg px-3 py-2 bg-[#F5F4FB] text-[#1A1A2E]"
        />
        <input
          type="date"
          value={filters.to}
          onChange={(e) => handleFilter('to', e.target.value)}
          className="text-sm font-body border border-[#E8E6F8] rounded-lg px-3 py-2 bg-[#F5F4FB] text-[#1A1A2E]"
        />
      </AdminFilterBar>

      {/* Logs table */}
      <div className="bg-white rounded-2xl border border-[#E8E6F8] shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="w-6 h-6 border-2 border-[#6C63FF] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : error ? (
          <div className="p-6 text-center">
            <p className="text-sm text-red-500 font-body">{error}</p>
            <button onClick={load} className="mt-2 text-xs text-[#6C63FF] underline font-body">Retry</button>
          </div>
        ) : data.logs.length === 0 ? (
          <p className="text-center text-[#9CA3AF] text-sm font-body py-12">
            No log entries match these filters.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm font-body">
              <thead>
                <tr className="bg-[#F5F4FB] border-b border-[#E8E6F8]">
                  {['When', 'Action', 'Description', 'Entity', 'IP', 'Status', ''].map((h) => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-heading font-semibold text-[#6B7280] whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E8E6F8]">
                {data.logs.map((log) => <LogRow key={log.id} log={log} />)}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {data.pages > 1 && (
        <div className="flex items-center justify-between mt-4">
          <p className="text-xs text-[#6B7280] font-body">
            Showing {(page - 1) * 50 + 1}–{Math.min(page * 50, data.total)} of {data.total} events
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="flex items-center gap-1 text-xs font-body px-3 py-1.5 border border-[#E8E6F8] rounded-lg hover:bg-[#F5F4FB] disabled:opacity-40"
            >
              <ChevronLeft className="h-3.5 w-3.5" /> Prev
            </button>
            <span className="text-xs font-body text-[#6B7280]">Page {page} / {data.pages}</span>
            <button
              onClick={() => setPage((p) => Math.min(data.pages, p + 1))}
              disabled={page === data.pages}
              className="flex items-center gap-1 text-xs font-body px-3 py-1.5 border border-[#E8E6F8] rounded-lg hover:bg-[#F5F4FB] disabled:opacity-40"
            >
              Next <ChevronRight className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
